import type { LandscapeKind } from '@/constants/content'

import { createNoise2D, createRandom, fbm, ridged } from '@/lib/procedural/noise'

type RGB = [number, number, number]

interface Camera {
    project: (x: number, y: number, z: number) => [number, number, number] | null
    groundAt: (sx: number, sy: number) => [number, number, number] | null
}

const HAZE: RGB = [231, 238, 240]
const SCAN = '#2eb3c8'

const clamp01 = (value: number): number => Math.min(1, Math.max(0, value))

const smooth = (edge0: number, edge1: number, x: number): number => {
    const t = clamp01((x - edge0) / (edge1 - edge0))

    return t * t * (3 - 2 * t)
}

const mixInto = (out: RGB, a: RGB, b: RGB, t: number): RGB => {
    const k = clamp01(t)

    out[0] = a[0] + (b[0] - a[0]) * k
    out[1] = a[1] + (b[1] - a[1]) * k
    out[2] = a[2] + (b[2] - a[2]) * k

    return out
}

const rgba = (color: RGB, alpha = 1): string => {
    return `rgba(${Math.round(color[0])}, ${Math.round(color[1])}, ${Math.round(color[2])}, ${alpha})`
}

const makeCamera = (width: number, height: number, eye: number, pitchDegrees: number, focalScale: number): Camera => {
    const pitch = (pitchDegrees * Math.PI) / 180
    const sinP = Math.sin(pitch)
    const cosP = Math.cos(pitch)
    const focal = height * focalScale
    const cx = width / 2
    const cy = height / 2

    return {
        project: (x, y, z) => {
            const dy = y - eye
            const yc = dy * cosP + z * sinP
            const zc = -dy * sinP + z * cosP

            if (zc <= 0.5) return null

            return [cx + (focal * x) / zc, cy - (focal * yc) / zc, zc]
        },
        groundAt: (sx, sy) => {
            const a = (sx - cx) / focal
            const b = -(sy - cy) / focal
            const dirY = b * cosP - sinP

            if (dirY >= -1e-4) return null

            const t = -eye / dirY

            return [t * a, t * (b * sinP + cosP), t]
        }
    }
}

/** Paints a per-pixel field at reduced resolution and scales it up smoothly. */
const paintField = (
    context: CanvasRenderingContext2D,
    width: number,
    height: number,
    step: number,
    colorAt: (sx: number, sy: number, out: RGB) => void
): void => {
    const w = Math.max(1, Math.ceil(width / step))
    const h = Math.max(1, Math.ceil(height / step))
    const scratch = document.createElement('canvas')

    scratch.width = w
    scratch.height = h

    const scratchContext = scratch.getContext('2d')

    if (!scratchContext) return

    const image = scratchContext.createImageData(w, h)
    const data = image.data
    const out: RGB = [0, 0, 0]

    for (let y = 0; y < h; y++) {
        for (let x = 0; x < w; x++) {
            colorAt(x * step + step / 2, y * step + step / 2, out)

            const index = (y * w + x) * 4

            data[index] = out[0]
            data[index + 1] = out[1]
            data[index + 2] = out[2]
            data[index + 3] = 255
        }
    }

    scratchContext.putImageData(image, 0, 0)
    context.imageSmoothingEnabled = true
    context.imageSmoothingQuality = 'high'
    context.drawImage(scratch, 0, 0, width, height)
}

const polygon = (context: CanvasRenderingContext2D, points: Array<[number, number, number] | null>): boolean => {
    if (points.some(point => point === null)) return false

    context.beginPath()

    points.forEach((point, index) => {
        if (!point) return

        if (index === 0) context.moveTo(point[0], point[1])
        else context.lineTo(point[0], point[1])
    })

    context.closePath()

    return true
}

const line3 = (context: CanvasRenderingContext2D, camera: Camera, a: [number, number, number], b: [number, number, number]): void => {
    const pa = camera.project(a[0], a[1], a[2])
    const pb = camera.project(b[0], b[1], b[2])

    if (!pa || !pb) return

    context.moveTo(pa[0], pa[1])
    context.lineTo(pb[0], pb[1])
}

const monoFont = (): string => {
    const family = getComputedStyle(document.documentElement).getPropertyValue('--font-app-mono').trim()

    return family || 'ui-monospace, monospace'
}

const tag = (context: CanvasRenderingContext2D, x: number, y: number, text: string, scale: number): void => {
    context.save()
    context.font = `500 ${Math.round(9.5 * scale)}px ${monoFont()}`
    context.textBaseline = 'middle'

    const padding = 5 * scale
    const metrics = context.measureText(text)
    const boxWidth = metrics.width + padding * 2
    const boxHeight = 16 * scale

    context.fillStyle = 'rgba(246, 248, 248, 0.92)'
    context.fillRect(x, y - boxHeight / 2, boxWidth, boxHeight)
    context.strokeStyle = SCAN
    context.lineWidth = Math.max(1, scale * 0.8)
    context.strokeRect(x, y - boxHeight / 2, boxWidth, boxHeight)
    context.fillStyle = '#1d2a31'
    context.fillText(text, x + padding, y + 0.5 * scale)
    context.restore()
}

const vignette = (context: CanvasRenderingContext2D, width: number, height: number): void => {
    const gradient = context.createLinearGradient(0, 0, 0, height)

    gradient.addColorStop(0, 'rgba(231, 238, 240, 0.55)')
    gradient.addColorStop(0.18, 'rgba(231, 238, 240, 0)')
    gradient.addColorStop(0.85, 'rgba(29, 42, 49, 0)')
    gradient.addColorStop(1, 'rgba(29, 42, 49, 0.08)')
    context.fillStyle = gradient
    context.fillRect(0, 0, width, height)
}

/* ------------------------------------------------------------------ */
/* Energy: rows of solar tables over sage farmland                     */
/* ------------------------------------------------------------------ */

const paintEnergy = (context: CanvasRenderingContext2D, width: number, height: number, scale: number): void => {
    const camera = makeCamera(width, height, 30, 48, 1.05)
    const noise = createNoise2D(11)
    const random = createRandom(12)

    const sage: RGB = [166, 182, 150]
    const light: RGB = [193, 204, 176]
    const deep: RGB = [138, 158, 128]
    const wheat: RGB = [205, 205, 170]
    const soil: RGB = [216, 210, 190]

    const edge = (z: number): number => 30 + 7 * Math.sin(z * 0.045 + 1.2) + 3 * Math.sin(z * 0.13)

    paintField(context, width, height, 2, (sx, sy, out) => {
        const ground = camera.groundAt(sx, sy)

        if (!ground) {
            out[0] = HAZE[0]
            out[1] = HAZE[1]
            out[2] = HAZE[2]

            return
        }

        const [gx, gz, depth] = ground
        const large = fbm(noise, gx * 0.011, gz * 0.011, 3)
        const fine = noise(gx * 0.42, gz * 0.42) * 0.5 + noise(gx * 1.3, gz * 1.3) * 0.25
        const parcel = noise(Math.floor((gx + 300) / 34) * 3.7, Math.floor((gz + 300) / 26) * 5.3)

        mixInto(out, sage, light, 0.5 + 0.5 * large)

        if (parcel > 0.35) mixInto(out, out, wheat, 0.55)
        else if (parcel < -0.4) mixInto(out, out, deep, 0.6)

        const shade = 1 + fine * 0.045

        out[0] *= shade
        out[1] *= shade
        out[2] *= shade

        const inside = Math.abs(gx) < edge(gz) && gz > 14 && gz < 118

        if (inside) mixInto(out, out, [176, 186, 160], 0.35)

        const roadX = Math.abs(gx) < 2.1 && gz > 10
        const roadZ = Math.abs(gz - 64) < 1.7
        const fieldBoundary = Math.abs(((gz + 300) % 26) - 13) > 12.85 && !inside

        if (roadX || roadZ) mixInto(out, out, soil, 0.92)
        else if (fieldBoundary) mixInto(out, out, deep, 0.5)

        mixInto(out, out, HAZE, smooth(50, 260, depth) * 0.88)
    })

    const rows: Array<number> = []

    for (let z = 116; z >= 15; z -= 2.6) rows.push(z)

    const panelBase: RGB = [44, 66, 79]
    const panelLight: RGB = [104, 132, 146]
    const flagged: Array<[number, number[]]> = []

    rows.forEach((z0, rowIndex) => {
        const limit = edge(z0)

        if (Math.abs(z0 - 64) < 2.6) return

        const depthProbe = camera.project(0, 0, z0)
        const fog = depthProbe ? smooth(50, 260, depthProbe[2]) : 1

        for (let x0 = -limit; x0 < limit - 5; x0 += 6) {
            if (x0 < 2.6 && x0 + 5.6 > -2.6) continue

            const x1 = x0 + 5.6
            const shadow = [
                camera.project(x0 + 0.4, 0, z0 + 1.5),
                camera.project(x1 + 0.4, 0, z0 + 1.5),
                camera.project(x1 + 0.4, 0, z0 + 2.5),
                camera.project(x0 + 0.4, 0, z0 + 2.5)
            ]

            if (polygon(context, shadow)) {
                context.fillStyle = `rgba(52, 66, 58, ${0.32 * (1 - fog)})`
                context.fill()
            }

            const face = [
                camera.project(x0, 0.5, z0),
                camera.project(x1, 0.5, z0),
                camera.project(x1, 1.7, z0 + 1.6),
                camera.project(x0, 1.7, z0 + 1.6)
            ]

            if (!polygon(context, face)) continue

            const tint = mixInto([0, 0, 0], panelBase, panelLight, 0.15 + random() * 0.12)

            mixInto(tint, tint, HAZE, fog * 0.92)
            context.fillStyle = rgba(tint)
            context.fill()

            const [a, b, c, d] = face as Array<[number, number, number]>

            context.beginPath()

            for (let m = 1; m < 5; m++) {
                const t = m / 5

                context.moveTo(a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t)
                context.lineTo(d[0] + (c[0] - d[0]) * t, d[1] + (c[1] - d[1]) * t)
            }

            context.moveTo((a[0] + d[0]) / 2, (a[1] + d[1]) / 2)
            context.lineTo((b[0] + c[0]) / 2, (b[1] + c[1]) / 2)
            context.strokeStyle = `rgba(150, 175, 188, ${0.45 * (1 - fog)})`
            context.lineWidth = Math.max(0.5, 0.6 * scale)
            context.stroke()

            context.beginPath()
            context.moveTo(d[0], d[1])
            context.lineTo(c[0], c[1])
            context.strokeStyle = `rgba(214, 228, 234, ${0.7 * (1 - fog)})`
            context.lineWidth = Math.max(0.6, 0.8 * scale)
            context.stroke()

            if ((rowIndex === 27 && x0 > 8 && x0 < 15) || (rowIndex === 33 && x0 < -12 && x0 > -19)) {
                flagged.push([rowIndex, [a[0], a[1], b[0], b[1], c[0], c[1], d[0], d[1]]])
            }
        }
    })

    flagged.forEach(([row, points], index) => {
        context.beginPath()
        context.moveTo(points[0] - 4 * scale, points[1] + 4 * scale)
        context.lineTo(points[2] + 4 * scale, points[3] + 4 * scale)
        context.lineTo(points[4] + 4 * scale, points[5] - 4 * scale)
        context.lineTo(points[6] - 4 * scale, points[7] - 4 * scale)
        context.closePath()
        context.strokeStyle = SCAN
        context.lineWidth = 1.4 * scale
        context.stroke()

        tag(context, points[2] + 10 * scale, points[3] - 6 * scale, index === 0 ? `ROW ${row} TILT 2.1°` : `ROW ${row} OK`, scale)
    })

    vignette(context, width, height)
}

/* ------------------------------------------------------------------ */
/* Infrastructure: truss bridge, river and lattice towers              */
/* ------------------------------------------------------------------ */

const paintInfrastructure = (context: CanvasRenderingContext2D, width: number, height: number, scale: number): void => {
    const camera = makeCamera(width, height, 38, 40, 1.1)
    const noise = createNoise2D(23)

    const riverX = (z: number): number => -6 + (z - 40) * 0.42 + 4.5 * Math.sin(z * 0.055)
    const water: RGB = [176, 199, 207]
    const waterDeep: RGB = [150, 178, 189]
    const bank: RGB = [212, 210, 196]
    const grass: RGB = [178, 190, 166]
    const field: RGB = [196, 202, 180]
    const road: RGB = [222, 224, 220]

    paintField(context, width, height, 2, (sx, sy, out) => {
        const ground = camera.groundAt(sx, sy)

        if (!ground) {
            out[0] = HAZE[0]
            out[1] = HAZE[1]
            out[2] = HAZE[2]

            return
        }

        const [gx, gz, depth] = ground
        const offset = gx - riverX(gz)
        const wobble = noise(gz * 0.08, 3.1) * 1.4
        const width0 = 8.5 + wobble
        const distance = Math.abs(offset)

        if (distance < width0) {
            const ripple = noise(gx * 0.9 + gz * 0.2, gz * 0.35) * 0.5 + 0.5

            mixInto(out, waterDeep, water, smooth(0, width0, distance) * 0.8 + ripple * 0.2)
        }
        else if (distance < width0 + 2.2) {
            mixInto(out, bank, grass, (distance - width0) / 2.2)
        }
        else {
            const n = fbm(noise, gx * 0.03, gz * 0.03, 3)
            const strips = Math.sin((gx * 0.6 + gz * 0.25) * 0.9) > 0.55 ? 1 : 0

            mixInto(out, grass, field, 0.5 + 0.5 * n)

            if (strips && offset > 0) mixInto(out, out, [186, 196, 170], 0.4)
        }

        if (Math.abs(gz - 42) < 1.9 && distance >= width0 - 0.5) mixInto(out, out, road, 0.95)
        if (Math.abs(gx + 30 - gz * 0.05) < 1.2) mixInto(out, out, road, 0.75)

        mixInto(out, out, HAZE, smooth(60, 300, depth) * 0.85)
    })

    const crossing = riverX(42)
    const xa = crossing - 13
    const xb = crossing + 13
    const deck = 1.6
    const panels = 10
    const topAt = (x: number): number => deck + 4.2 + 1.8 * Math.sin(((x - xa) / (xb - xa)) * Math.PI)

    /* Shadow of the truss on the water */
    context.beginPath()

    for (const z of [40.6, 43.4]) {
        for (let i = 0; i <= panels; i++) {
            const x = xa + ((xb - xa) * i) / panels
            const top = topAt(x)

            line3(context, camera, [x + deck * 0.5, 0, z + deck * 0.6], [x + top * 0.5, 0, z + top * 0.6])
        }
    }

    context.strokeStyle = 'rgba(40, 60, 70, 0.16)'
    context.lineWidth = 1.2 * scale
    context.stroke()

    /* Deck */
    const deckFace = [
        camera.project(xa - 2, deck, 40.4),
        camera.project(xb + 2, deck, 40.4),
        camera.project(xb + 2, deck, 43.6),
        camera.project(xa - 2, deck, 43.6)
    ]

    if (polygon(context, deckFace)) {
        context.fillStyle = '#c9d0d3'
        context.fill()
        context.strokeStyle = 'rgba(29, 42, 49, 0.5)'
        context.lineWidth = 0.8 * scale
        context.stroke()
    }

    /* Two Parker trusses with cross bracing */
    context.beginPath()

    for (const z of [40.6, 43.4]) {
        for (let i = 0; i < panels; i++) {
            const x0 = xa + ((xb - xa) * i) / panels
            const x1 = xa + ((xb - xa) * (i + 1)) / panels

            line3(context, camera, [x0, deck, z], [x1, deck, z])
            line3(context, camera, [x0, topAt(x0), z], [x1, topAt(x1), z])
            line3(context, camera, [x0, deck, z], [x0, topAt(x0), z])

            if (i < panels / 2) line3(context, camera, [x0, topAt(x0), z], [x1, deck, z])
            else line3(context, camera, [x0, deck, z], [x1, topAt(x1), z])
        }

        line3(context, camera, [xb, deck, z], [xb, topAt(xb), z])
    }

    for (let i = 0; i <= panels; i++) {
        const x = xa + ((xb - xa) * i) / panels
        const top = topAt(x)

        line3(context, camera, [x, top, 40.6], [x, top, 43.4])
        line3(context, camera, [x, deck, 40.6], [x, deck, 43.4])

        if (i < panels) {
            const next = xa + ((xb - xa) * (i + 1)) / panels

            line3(context, camera, [x, top, 40.6], [next, topAt(next), 43.4])
            line3(context, camera, [x, top, 43.4], [next, topAt(next), 40.6])
        }
    }

    context.strokeStyle = 'rgba(29, 42, 49, 0.82)'
    context.lineWidth = 1 * scale
    context.stroke()

    /* Lattice transmission towers with sagging conductors */
    const towers: Array<[number, number]> = [[-34, 18], [-30, 50], [-26, 84], [-22, 120]]
    const tips: Array<Array<[number, number, number]>> = []

    context.beginPath()

    towers.forEach(([tx, tz]) => {
        const h = 15
        const half = (y: number): number => 1.3 - 0.95 * (y / h)
        const corners: Array<[number, number]> = [[-1, -1], [1, -1], [1, 1], [-1, 1]]

        for (const [cx, cz] of corners) {
            line3(context, camera, [tx + cx * half(0), 0, tz + cz * half(0)], [tx + cx * half(h), h, tz + cz * half(h)])
        }

        for (let level = 0; level < 6; level++) {
            const y0 = (level / 6) * h
            const y1 = ((level + 1) / 6) * h

            for (let side = 0; side < 4; side++) {
                const [ax, az] = corners[side]
                const [bx, bz] = corners[(side + 1) % 4]

                line3(context, camera, [tx + ax * half(y0), y0, tz + az * half(y0)], [tx + bx * half(y1), y1, tz + bz * half(y1)])
                line3(context, camera, [tx + bx * half(y0), y0, tz + bz * half(y0)], [tx + ax * half(y1), y1, tz + az * half(y1)])
            }
        }

        const armY = h * 0.94

        line3(context, camera, [tx - 4.2, armY, tz], [tx + 4.2, armY, tz])
        line3(context, camera, [tx - 3, armY - 2.4, tz], [tx + 3, armY - 2.4, tz])

        tips.push([[tx - 4.2, armY, tz], [tx + 4.2, armY, tz], [tx - 3, armY - 2.4, tz], [tx + 3, armY - 2.4, tz]])
    })

    context.strokeStyle = 'rgba(29, 42, 49, 0.62)'
    context.lineWidth = 0.75 * scale
    context.stroke()

    context.beginPath()

    for (let t = 0; t < tips.length - 1; t++) {
        for (let w = 0; w < 4; w++) {
            const a = tips[t][w]
            const b = tips[t + 1][w]

            let previous: [number, number, number] | null = null

            for (let s = 0; s <= 24; s++) {
                const k = s / 24
                const point = camera.project(a[0] + (b[0] - a[0]) * k, a[1] + (b[1] - a[1]) * k - Math.sin(k * Math.PI) * 2.6, a[2] + (b[2] - a[2]) * k)

                if (point && previous) {
                    context.moveTo(previous[0], previous[1])
                    context.lineTo(point[0], point[1])
                }

                previous = point
            }
        }
    }

    context.strokeStyle = 'rgba(29, 42, 49, 0.35)'
    context.lineWidth = 0.6 * scale
    context.stroke()

    const mid = camera.project(crossing, topAt(crossing) + 0.6, 40.6)

    if (mid) {
        context.beginPath()
        context.arc(mid[0], mid[1], 7 * scale, 0, Math.PI * 2)
        context.strokeStyle = SCAN
        context.lineWidth = 1.4 * scale
        context.stroke()
        tag(context, mid[0] + 12 * scale, mid[1] - 10 * scale, 'MIDSPAN -4.2 MM', scale)
    }

    vignette(context, width, height)
}

/* ------------------------------------------------------------------ */
/* Industry: dense plant grid, tanks and pipe racks                    */
/* ------------------------------------------------------------------ */

interface Box {
    x0: number
    z0: number
    x1: number
    z1: number
    h: number
    roof: RGB
}

interface Tank {
    x: number
    z: number
    r: number
    h: number
}

const paintIndustry = (context: CanvasRenderingContext2D, width: number, height: number, scale: number): void => {
    const camera = makeCamera(width, height, 92, 64, 1.15)
    const noise = createNoise2D(31)
    const random = createRandom(32)

    const concrete: RGB = [214, 219, 219]
    const asphalt: RGB = [196, 202, 204]
    const lane: RGB = [232, 235, 234]
    const blockSize = 22
    const roadHalf = 2

    paintField(context, width, height, 2, (sx, sy, out) => {
        const ground = camera.groundAt(sx, sy)

        if (!ground) {
            out[0] = HAZE[0]
            out[1] = HAZE[1]
            out[2] = HAZE[2]

            return
        }

        const [gx, gz, depth] = ground
        const lx = ((gx % blockSize) + blockSize) % blockSize
        const lz = ((gz % blockSize) + blockSize) % blockSize
        const onRoad = lx < roadHalf * 2 || lz < roadHalf * 2
        const grain = noise(gx * 0.5, gz * 0.5) * 0.5 + 0.5

        if (onRoad) {
            mixInto(out, asphalt, lane, grain * 0.25)

            if ((Math.abs(lx - roadHalf) < 0.12 && Math.floor(gz) % 3 === 0) || (Math.abs(lz - roadHalf) < 0.12 && Math.floor(gx) % 3 === 0)) {
                mixInto(out, out, [246, 246, 240], 0.9)
            }
        }
        else {
            mixInto(out, concrete, [204, 210, 210], grain)
        }

        mixInto(out, out, HAZE, smooth(90, 260, depth) * 0.8)
    })

    const boxes: Array<Box> = []
    const tanks: Array<Tank> = []
    const roofs: Array<RGB> = [[201, 209, 212], [188, 198, 202], [214, 220, 222], [172, 184, 190], [222, 226, 226]]

    for (let bx = -6; bx <= 6; bx++) {
        for (let bz = 0; bz <= 9; bz++) {
            const x0 = bx * blockSize + roadHalf * 2 + 0.8
            const z0 = bz * blockSize + roadHalf * 2 + 0.8
            const size = blockSize - roadHalf * 2 - 1.6
            const kind = noise(bx * 0.73 + 5, bz * 0.61 + 9)

            if (kind > 0.38) {
                const count = 2 + Math.floor(random() * 2)
                const r = size / (count * 2) - 0.6

                for (let i = 0; i < count; i++) {
                    for (let j = 0; j < count; j++) {
                        tanks.push({ x: x0 + r + 0.6 + i * (r * 2 + 1.2), z: z0 + r + 0.6 + j * (r * 2 + 1.2), r, h: 3 + random() * 3 })
                    }
                }
            }
            else if (kind < -0.45) {
                for (let lot = 0; lot < 6; lot++) {
                    boxes.push({ x0: x0 + lot * (size / 6) + 0.4, z0: z0 + 1, x1: x0 + (lot + 1) * (size / 6) - 0.4, z1: z0 + size - 1, h: 0.25, roof: [222, 225, 224] })
                }
            }
            else {
                const split = 0.35 + random() * 0.3

                if (random() > 0.5) {
                    boxes.push({ x0, z0, x1: x0 + size * split - 0.6, z1: z0 + size, h: 3 + random() * 9, roof: roofs[Math.floor(random() * roofs.length)] })
                    boxes.push({ x0: x0 + size * split + 0.6, z0, x1: x0 + size, z1: z0 + size * 0.6, h: 2 + random() * 6, roof: roofs[Math.floor(random() * roofs.length)] })
                    boxes.push({ x0: x0 + size * split + 0.6, z0: z0 + size * 0.6 + 1, x1: x0 + size, z1: z0 + size, h: 1.5 + random() * 3, roof: roofs[Math.floor(random() * roofs.length)] })
                }
                else {
                    boxes.push({ x0, z0, x1: x0 + size, z1: z0 + size * split, h: 4 + random() * 8, roof: roofs[Math.floor(random() * roofs.length)] })
                    boxes.push({ x0, z0: z0 + size * split + 1, x1: x0 + size, z1: z0 + size, h: 2 + random() * 5, roof: roofs[Math.floor(random() * roofs.length)] })
                }
            }
        }
    }

    type Drawable = { depth: number, draw: () => void }

    const drawables: Array<Drawable> = []

    boxes.forEach((box) => {
        const center = camera.project((box.x0 + box.x1) / 2, 0, (box.z0 + box.z1) / 2)

        if (!center) return

        drawables.push({
            depth: center[2],
            draw: () => {
                const fog = smooth(90, 260, center[2])
                const shadow = [
                    camera.project(box.x0 + box.h * 0.35, 0, box.z0 + box.h * 0.25),
                    camera.project(box.x1 + box.h * 0.35, 0, box.z0 + box.h * 0.25),
                    camera.project(box.x1 + box.h * 0.35, 0, box.z1 + box.h * 0.25),
                    camera.project(box.x0 + box.h * 0.35, 0, box.z1 + box.h * 0.25)
                ]

                if (polygon(context, shadow)) {
                    context.fillStyle = `rgba(29, 42, 49, ${0.12 * (1 - fog)})`
                    context.fill()
                }

                const front = [
                    camera.project(box.x0, 0, box.z0),
                    camera.project(box.x1, 0, box.z0),
                    camera.project(box.x1, box.h, box.z0),
                    camera.project(box.x0, box.h, box.z0)
                ]
                const sideX = (box.x0 + box.x1) / 2 > 0 ? box.x0 : box.x1
                const side = [
                    camera.project(sideX, 0, box.z0),
                    camera.project(sideX, 0, box.z1),
                    camera.project(sideX, box.h, box.z1),
                    camera.project(sideX, box.h, box.z0)
                ]

                const wall = mixInto([0, 0, 0], [150, 162, 168], HAZE, fog * 0.85)

                if (polygon(context, side)) {
                    context.fillStyle = rgba(wall)
                    context.fill()
                }

                if (polygon(context, front)) {
                    context.fillStyle = rgba(mixInto([0, 0, 0], wall, [255, 255, 255], 0.2))
                    context.fill()
                }

                const roof = [
                    camera.project(box.x0, box.h, box.z0),
                    camera.project(box.x1, box.h, box.z0),
                    camera.project(box.x1, box.h, box.z1),
                    camera.project(box.x0, box.h, box.z1)
                ]

                if (polygon(context, roof)) {
                    context.fillStyle = rgba(mixInto([0, 0, 0], box.roof, HAZE, fog * 0.85))
                    context.fill()
                    context.strokeStyle = `rgba(29, 42, 49, ${0.22 * (1 - fog)})`
                    context.lineWidth = 0.6 * scale
                    context.stroke()
                }
            }
        })
    })

    tanks.forEach((tank) => {
        const center = camera.project(tank.x, 0, tank.z)

        if (!center) return

        drawables.push({
            depth: center[2],
            draw: () => {
                const fog = smooth(90, 260, center[2])
                const ring = (y: number, radius: number): Array<[number, number, number] | null> => {
                    return Array.from({ length: 28 }, (_, i) => {
                        const angle = (i / 28) * Math.PI * 2

                        return camera.project(tank.x + Math.cos(angle) * radius, y, tank.z + Math.sin(angle) * radius)
                    })
                }

                const shadow = Array.from({ length: 28 }, (_, i) => {
                    const angle = (i / 28) * Math.PI * 2

                    return camera.project(tank.x + Math.cos(angle) * tank.r + tank.h * 0.35, 0, tank.z + Math.sin(angle) * tank.r + tank.h * 0.25)
                })

                if (polygon(context, shadow)) {
                    context.fillStyle = `rgba(29, 42, 49, ${0.12 * (1 - fog)})`
                    context.fill()
                }

                if (polygon(context, ring(0, tank.r))) {
                    context.fillStyle = rgba(mixInto([0, 0, 0], [160, 172, 178], HAZE, fog * 0.85))
                    context.fill()
                }

                const top = ring(tank.h, tank.r)

                if (polygon(context, top)) {
                    context.fillStyle = rgba(mixInto([0, 0, 0], [226, 231, 232], HAZE, fog * 0.85))
                    context.fill()
                    context.strokeStyle = `rgba(29, 42, 49, ${0.3 * (1 - fog)})`
                    context.lineWidth = 0.7 * scale
                    context.stroke()
                }

                if (polygon(context, ring(tank.h, tank.r * 0.55))) {
                    context.strokeStyle = `rgba(29, 42, 49, ${0.18 * (1 - fog)})`
                    context.lineWidth = 0.6 * scale
                    context.stroke()
                }
            }
        })
    })

    drawables.sort((a, b) => b.depth - a.depth).forEach(item => item.draw())

    /* Pipe racks along two avenues */
    context.beginPath()

    for (const offset of [-0.5, 0, 0.5]) {
        line3(context, camera, [-6 * blockSize, 2.4, 3 * blockSize + roadHalf + offset], [7 * blockSize, 2.4, 3 * blockSize + roadHalf + offset])
        line3(context, camera, [blockSize + roadHalf + offset, 2.4, 0], [blockSize + roadHalf + offset, 2.4, 10 * blockSize])
    }

    context.strokeStyle = 'rgba(29, 42, 49, 0.45)'
    context.lineWidth = 0.9 * scale
    context.stroke()

    const highlight = tanks.find(tank => tank.z > 3 * blockSize && tank.z < 5 * blockSize && Math.abs(tank.x) < 40)

    if (highlight) {
        const point = camera.project(highlight.x, highlight.h, highlight.z)

        if (point) {
            const radius = Math.abs((camera.project(highlight.x + highlight.r, highlight.h, highlight.z)?.[0] ?? point[0] + 10) - point[0]) + 6 * scale

            context.beginPath()
            context.arc(point[0], point[1], radius, 0, Math.PI * 2)
            context.strokeStyle = SCAN
            context.lineWidth = 1.4 * scale
            context.stroke()
            tag(context, point[0] + radius + 6 * scale, point[1] - radius * 0.6, 'TANK 07 SHELL OK', scale)
        }
    }

    vignette(context, width, height)
}

/* ------------------------------------------------------------------ */
/* Geospatial: hillshaded contour map                                  */
/* ------------------------------------------------------------------ */

const paintGeospatial = (context: CanvasRenderingContext2D, width: number, height: number, scale: number): void => {
    const noise = createNoise2D(41)
    const cell = Math.max(4, Math.round(5 * scale))
    const cols = Math.ceil(width / cell) + 1
    const rows = Math.ceil(height / cell) + 1
    const field = new Float32Array(cols * rows)
    const unit = 1 / Math.max(width, height)

    const heightAt = (px: number, py: number): number => {
        const x = px * unit * 3.2
        const y = py * unit * 3.2
        const ridge = ridged(noise, x * 0.9 + 3, y * 0.9 + 7, 5)
        const base = fbm(noise, x * 0.6 + 11, y * 0.6 - 4, 4) * 0.5 + 0.5

        return ridge * 0.62 + base * 0.48
    }

    for (let j = 0; j < rows; j++) {
        for (let i = 0; i < cols; i++) {
            field[j * cols + i] = heightAt(i * cell, j * cell)
        }
    }

    const sample = (px: number, py: number): number => {
        const fx = Math.min(cols - 1.001, Math.max(0, px / cell))
        const fy = Math.min(rows - 1.001, Math.max(0, py / cell))
        const i = Math.floor(fx)
        const j = Math.floor(fy)
        const tx = fx - i
        const ty = fy - j
        const a = field[j * cols + i]
        const b = field[j * cols + i + 1]
        const c = field[(j + 1) * cols + i]
        const d = field[(j + 1) * cols + i + 1]

        return (a + (b - a) * tx) * (1 - ty) + (c + (d - c) * tx) * ty
    }

    const lowLand: RGB = [222, 228, 224]
    const highLand: RGB = [240, 242, 238]
    const lake: RGB = [196, 216, 224]
    const waterLevel = 0.36

    paintField(context, width, height, 2, (sx, sy, out) => {
        const h = sample(sx, sy)
        const dx = sample(sx + 2, sy) - sample(sx - 2, sy)
        const dy = sample(sx, sy + 2) - sample(sx, sy - 2)
        const shade = clamp01(0.62 + (-dx * 0.7 + -dy * 0.7) * 22)

        if (h < waterLevel) {
            mixInto(out, lake, [214, 228, 233], (h - (waterLevel - 0.12)) / 0.12)

            return
        }

        mixInto(out, lowLand, highLand, (h - waterLevel) * 1.6)

        const k = 0.78 + shade * 0.32

        out[0] = Math.min(255, out[0] * k)
        out[1] = Math.min(255, out[1] * k)
        out[2] = Math.min(255, out[2] * k)
    })

    /* Grid */
    context.beginPath()

    const gridStep = width / 8

    for (let x = gridStep; x < width; x += gridStep) {
        context.moveTo(x, 0)
        context.lineTo(x, height)
    }

    for (let y = gridStep; y < height; y += gridStep) {
        context.moveTo(0, y)
        context.lineTo(width, y)
    }

    context.strokeStyle = 'rgba(29, 42, 49, 0.07)'
    context.lineWidth = 1
    context.stroke()

    /* Contours via marching squares */
    const interval = 0.024

    for (let level = waterLevel; level < 1.2; level += interval) {
        const index = Math.round((level - waterLevel) / interval)
        const major = index % 5 === 0

        context.beginPath()

        for (let j = 0; j < rows - 1; j++) {
            for (let i = 0; i < cols - 1; i++) {
                const a = field[j * cols + i]
                const b = field[j * cols + i + 1]
                const c = field[(j + 1) * cols + i + 1]
                const d = field[(j + 1) * cols + i]
                const code = (a > level ? 8 : 0) | (b > level ? 4 : 0) | (c > level ? 2 : 0) | (d > level ? 1 : 0)

                if (code === 0 || code === 15) continue

                const x = i * cell
                const y = j * cell
                const top: [number, number] = [x + cell * ((level - a) / (b - a)), y]
                const right: [number, number] = [x + cell, y + cell * ((level - b) / (c - b))]
                const bottom: [number, number] = [x + cell * ((level - d) / (c - d)), y + cell]
                const left: [number, number] = [x, y + cell * ((level - a) / (d - a))]

                const segment = (p: [number, number], q: [number, number]): void => {
                    context.moveTo(p[0], p[1])
                    context.lineTo(q[0], q[1])
                }

                switch (code) {
                    case 1: case 14: segment(left, bottom); break
                    case 2: case 13: segment(bottom, right); break
                    case 3: case 12: segment(left, right); break
                    case 4: case 11: segment(top, right); break
                    case 5: segment(left, top); segment(bottom, right); break
                    case 6: case 9: segment(top, bottom); break
                    case 7: case 8: segment(left, top); break
                    case 10: segment(left, bottom); segment(top, right); break
                    default: break
                }
            }
        }

        context.strokeStyle = major ? 'rgba(29, 42, 49, 0.62)' : 'rgba(29, 42, 49, 0.26)'
        context.lineWidth = (major ? 1.15 : 0.6) * scale
        context.stroke()
    }

    /* Shoreline */
    context.beginPath()

    for (let j = 0; j < rows - 1; j++) {
        for (let i = 0; i < cols - 1; i++) {
            const a = field[j * cols + i]
            const b = field[j * cols + i + 1]

            if ((a - waterLevel) * (b - waterLevel) < 0) {
                const x = i * cell + cell * ((waterLevel - a) / (b - a))

                context.moveTo(x, j * cell)
                context.lineTo(x, j * cell + cell)
            }
        }
    }

    context.strokeStyle = 'rgba(46, 179, 200, 0.35)'
    context.lineWidth = 0.8 * scale
    context.stroke()

    /* Spot heights at local summits */
    const peaks: Array<[number, number, number]> = []
    const probe = Math.round(28 * scale)

    for (let y = probe; y < height - probe; y += probe) {
        for (let x = probe; x < width - probe; x += probe) {
            let bestX = x
            let bestY = y
            let best = sample(x, y)

            for (let k = 0; k < 12; k++) {
                const nx = bestX + (sample(bestX + 3, bestY) - sample(bestX - 3, bestY) > 0 ? 3 : -3)
                const ny = bestY + (sample(bestX, bestY + 3) - sample(bestX, bestY - 3) > 0 ? 3 : -3)
                const value = sample(nx, ny)

                if (value <= best) break

                best = value
                bestX = nx
                bestY = ny
            }

            if (best > 0.82 && !peaks.some(peak => Math.hypot(peak[0] - bestX, peak[1] - bestY) < 90 * scale)) {
                peaks.push([bestX, bestY, best])
            }
        }
    }

    context.save()
    context.font = `500 ${Math.round(9 * scale)}px ${monoFont()}`
    context.fillStyle = '#1d2a31'

    peaks.slice(0, 6).forEach(([x, y, value]) => {
        context.beginPath()
        context.moveTo(x, y - 4 * scale)
        context.lineTo(x + 3.6 * scale, y + 2.4 * scale)
        context.lineTo(x - 3.6 * scale, y + 2.4 * scale)
        context.closePath()
        context.fill()
        context.fillText(`${Math.round(1800 + value * 1200)}`, x + 6 * scale, y + 2 * scale)
    })

    context.restore()

    /* Survey boundary */
    context.save()
    context.setLineDash([6 * scale, 5 * scale])
    context.strokeStyle = SCAN
    context.lineWidth = 1.3 * scale
    context.beginPath()
    context.moveTo(width * 0.18, height * 0.24)
    context.lineTo(width * 0.62, height * 0.16)
    context.lineTo(width * 0.84, height * 0.46)
    context.lineTo(width * 0.66, height * 0.82)
    context.lineTo(width * 0.24, height * 0.72)
    context.closePath()
    context.stroke()
    context.restore()

    tag(context, width * 0.62 + 8 * scale, height * 0.16 - 12 * scale, 'AOI 96 KM²', scale)

    vignette(context, width, height)
}

const PAINTERS: Record<LandscapeKind, (context: CanvasRenderingContext2D, width: number, height: number, scale: number) => void> = {
    energy: paintEnergy,
    infrastructure: paintInfrastructure,
    industry: paintIndustry,
    geospatial: paintGeospatial
}

/** Renders one procedural aerial landscape into the canvas at its current CSS size. */
export const paintLandscape = (canvas: HTMLCanvasElement, kind: LandscapeKind): void => {
    const rect = canvas.getBoundingClientRect()
    const ratio = Math.min(window.devicePixelRatio || 1, 2)
    const width = Math.max(2, Math.round(rect.width * ratio))
    const height = Math.max(2, Math.round(rect.height * ratio))

    canvas.width = width
    canvas.height = height

    const context = canvas.getContext('2d')

    if (!context) return

    context.clearRect(0, 0, width, height)
    PAINTERS[kind](context, width, height, ratio)
}
